import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import { signInWithGoogle } from '../firebase';
import { IN_TESTING } from '../config/flags';
import { WindowCloseIcon, SignInIcon } from './Icons';
import { evaluateNextOptimalAssessment } from '../utils/triageEngine';

export default function SupervisionTriageModal({ isOpen, onClose }) {
    const { isPremium, user, history } = useAuth();
    const [step, setStep] = useState(1);
    const [pathway, setPathway] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setPathway(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const timeGreeting = (() => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        if (hour < 22) return "Good evening";
        return "Working late?";
    })();

    const getPathwayLabel = () => {
        if (pathway === 'focus') return "🎯 Finding Focus & Direction";
        if (pathway === 'complexity') return "🧩 Untangling Complexity";
        if (pathway === 'perspective') return "👁️ Understanding Emotions & Others";
        return "";
    };

    const ACCENT_COLOR = 'var(--triage-accent)'; 

    const styles = {
        botBubble: { 
            background: 'var(--bg-card)', 
            color: 'var(--text-main)', 
            padding: '14px 18px', 
            borderRadius: '20px 20px 20px 4px', 
            fontSize: '1.05rem', 
            lineHeight: 1.5, 
            maxWidth: '85%', 
            boxShadow: '0 2px 8px var(--card-shadow)' 
        },
        userBubble: { 
            alignSelf: 'flex-end', 
            background: 'var(--triage-accent-bg)', 
            color: 'var(--text-main)', 
            padding: '14px 20px', 
            borderRadius: '20px 20px 4px 20px', 
            fontSize: '1.05rem', 
            fontWeight: '600', 
            maxWidth: '85%',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 4px 12px var(--card-shadow)'
        },
        quickReplyBtn: { 
            background: 'transparent', 
            border: `1.5px solid ${ACCENT_COLOR}`, 
            borderRadius: '24px', 
            padding: '14px 20px', 
            color: ACCENT_COLOR, 
            textAlign: 'left', 
            cursor: 'pointer', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '4px',
            transition: 'all 0.2s ease'
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'var(--bg-main)', zIndex: 9999, display: 'flex', flexDirection: 'column', padding: '20px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '600px', margin: '0 auto 20px auto', paddingBottom: '15px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img src="/icon.png" alt="Concierge" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                    <div>
                        <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--text-main)' }}>Reflective Practice Guide</div>
                        <div style={{ fontSize: '12px', color: '#10b981', fontWeight: '500' }}>Active Now</div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {(!isPremium && !IN_TESTING) && (
                        <button 
                            onClick={() => window.location.href = '/?triage=closed&view=pricing'}
                            style={{ background: 'var(--card-bg)', color: 'var(--text-main)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '6px 14px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 6px var(--card-shadow)' }}
                        >
                            Buy Pro License (from $2/mo)
                        </button>
                    )}
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }} title="Close">
                        <WindowCloseIcon size="18px" />
                    </button>
                </div>
            </div>

            <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto' }}>
                <div className="animate-slide-up" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <div style={styles.botBubble}>
                        {timeGreeting}{user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}. 
                        {(() => {
                            if (!user || !Array.isArray(history) || history.length === 0) {
                                return " Supervision requires a clear headspace. Where are you feeling stuck today?";
                            }
                            const evalResult = evaluateNextOptimalAssessment(history, user.displayName);
                            if (evalResult.status === 'insufficient_data') {
                                return " You have 1 assessment logged. Exploring a reflective tool today will help build your self-supervision foundation.";
                            }
                            return " Where are you feeling stuck or seeking perspective today?";
                        })()}
                    </div>
                </div>

                {step === 1 && (
                    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <button onClick={() => { setPathway('focus'); setStep(2); }} style={styles.quickReplyBtn}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>🎯 Finding Focus & Direction</span>
                            <span style={{ fontSize: '12px', opacity: 0.8 }}>I feel aimless, unaligned, or unsure what to talk about.</span>
                        </button>
                        <button onClick={() => { setPathway('complexity'); setStep(2); }} style={styles.quickReplyBtn}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>🧩 Untangling Complexity</span>
                            <span style={{ fontSize: '12px', opacity: 0.8 }}>I am overwhelmed by a situation with too many moving parts.</span>
                        </button>
                        <button onClick={() => { setPathway('perspective'); setStep(2); }} style={styles.quickReplyBtn}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>👁️ Understanding Emotions & Others</span>
                            <span style={{ fontSize: '12px', opacity: 0.8 }}>I am struggling to understand a reaction or gut feeling.</span>
                        </button>

                        {!user && (
                            <button onClick={async () => { try { await signInWithGoogle(); } catch { onClose(); } }} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px', marginTop: '15px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', margin: '15px auto 0 auto' }}>
                                <SignInIcon size="14px" />
                                <span>Sign in to unlock history-based routing</span>
                            </button>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <>
                        <div className="animate-slide-up" style={styles.userBubble}>{getPathwayLabel()}</div>
                        <div className="animate-slide-up" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                            <div style={styles.botBubble}>Understood. Which of these sounds most accurate right now?</div>
                        </div>

                        <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {pathway === 'focus' && (
                                <>
                                    <button onClick={() => window.location.href = '/?tool=fourbatteries&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want a 30-second check-in on my physical, mental, emotional, and purpose fuel levels."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch 4-Battery Check-In</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=bandwidthgauge&from=supervision'} style={{ ...styles.quickReplyBtn, marginTop: '8px' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I need to evaluate my workload capacity (0-10) and isolate specific demand drivers."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Workload Bandwidth Gauge</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=energyaudit&from=supervision'} style={{ ...styles.quickReplyBtn, marginTop: '8px' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to audit my weekly calendar as a lab report and score energy drains vs. boosts."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Weekly Energy Audit</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=headlines&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to synthesize a session breakthrough into a clear headline &amp; action plan."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Headlines</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=target&labels=personal,relationships,work&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I feel misaligned or wondering how close I am to my professional center."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Target Tool</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=focuscards&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to flip through cards to help discover my focal point to bring to supervision."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Focus Cards</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=bridge&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to track how my thinking evolved before and after a supervision session or experience."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch 3-2-1 Bridge</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=usedtothink&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to reflect on how my clinical judgment or assumptions have shifted over time."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch I Used to Think... Now I Think</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=3ys&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I feel aimless or disconnected from the bigger picture of my work."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch The 3Ys</div>
                                    </button>
                                </>
                            )}

                            {pathway === 'complexity' && (
                                <>
                                    <button onClick={() => window.location.href = '/?tool=connected&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to nominate a central theme and map connected items radiating from it."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Connected Items</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=generatesort&from=supervision'} style={{ ...styles.quickReplyBtn, marginTop: '8px' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I need to map out and organize a complex professional scenario, project, or student case."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Generate, Sort, Connect, Elaborate</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=complexity&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I am overwhelmed by a problem with too many moving parts."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Complexity Assessment</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=stickingpoints&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I am dealing with a messy controversy, interpersonal dispute, or policy deadlock."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Sticking Points</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=tugofwar&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I am torn between two competing pressures or an ethical dilemma."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Tug-of-War</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=claimsupport&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to test a clinical claim or diagnostic assumption against evidence."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Claim, Support, Question</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=compass&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I am anxious or stuck regarding a new idea, direction, or proposition."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Compass Points</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=cec&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I feel like I'm absorbing information but need to synthesize new insights."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Connect, Extend, Challenge</div>
                                    </button>
                                </>
                            )}

                            {pathway === 'perspective' && (
                                <>
                                    <button onClick={() => window.location.href = '/?tool=trueforwho&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to examine a policy, claim, or best-practice assumption through cultural and power lenses."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch True for Who?</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=circleofviewpoints&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I need to explore a case or situation from multiple stakeholder perspectives."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Circle of Viewpoints</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=stepinside&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to step into one specific person's shoes to gain deep empathy."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Step Inside</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=traffic&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I am uncertain about the 'health' or risk level of a case."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Traffic Lights</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=morethanwords&from=supervision'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I cannot find the right language for a complex gut feeling."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch More Than Words</div>
                                    </button>
                                </>
                            )}
                            <button onClick={() => setStep(1)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', marginTop: '10px' }}>← Start over</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}